import { useEffect, useState } from 'react';
import { Configuration, SkusApi } from '@/api';
import { SkuFilters, SkuSort } from '@/api/SkusApi';
import { useSettings } from '@/contexts/SettingsContext';
import { EmptyResult } from '@/models/Pagination';
import { Sku, SkuAssociation, Skus } from '@/models/Sku';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { setPaginationResultIfNotLoading } from '@/utils/pagination';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState = EmptyResult<Sku>();

const useFindSkusQuery = (
  page: number,
  searchQuery?: string,
  filters?: Partial<SkuFilters>,
  sort?: SkuSort[],
  include: SkuAssociation[] = [SkuAssociation.PRODUCT],
  enabled = true,
  storeId?: string | null,
) => {
  const queryClient = useQueryClient();

  const [skus, setSkus] = useState<Skus>(initialState);

  const { currencyCode, languageCode } = useSettings();
  const skusApi = new SkusApi(new Configuration(useAuth), { currencyCode, languageCode });

  const itemsPerPage =
    Array.isArray(filters?.skuId) && filters?.skuId.length > 0 ? filters?.skuId.length : 10;
  const findFilters: Partial<SkuFilters> = {
    ...filters,
    storeId: storeId ?? filters?.storeId,
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['skus', page, searchQuery, findFilters, currencyCode, languageCode],
    queryFn: () => {
      const offset = page * itemsPerPage - itemsPerPage;
      return skusApi.findSkus(searchQuery, findFilters, offset, itemsPerPage, sort, include);
    },
    enabled,
  });

  const invalidateQuery = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['skus', page, searchQuery, findFilters],
    });
  };

  useEffect(() => {
    setPaginationResultIfNotLoading(data, setSkus, isLoading);
  }, [data, isLoading, isFetching]);

  return {
    skus,
    isLoading,
    isFetching,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useFindSkusQuery;
